import { memo, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Clock,
  Loader2,
  Lock,
  Trash2,
} from 'lucide-react';
import { Badge } from '../ui/Badge';
import { ApiError } from '../../lib/api';
import {
  composicionApi,
  composicionKeys,
  type ActividadArbol,
  type EdicionActividad,
  type HerramientaCatalogo,
  type MoverActividad,
} from '../../lib/composicion';
import { useArrastreCompositor } from '../../hooks/useArrastre';
import { ConexionInputs, inputsDe, satisfaceAlgunInput } from './ConexionInputs';
import { useResaltado } from './ValidadorPanel';

/** Sesión candidata como destino de «mover a otra sesión». */
export interface OpcionSesion {
  id: string;
  etiqueta: string;
  /** Actividades que ya tiene: el movimiento entra al final (orden n+1). */
  totalActividades: number;
}

const botonIcono =
  'flex min-h-touch min-w-touch shrink-0 items-center justify-center rounded-md ' +
  'text-content-muted transition-colors duration-fast hover:bg-surface-raised ' +
  'hover:text-content focus-visible:outline-none focus-visible:shadow-focus ' +
  'disabled:pointer-events-none disabled:opacity-40';

const claseCampo =
  'rounded-md border border-border-strong bg-surface-raised px-2 py-1 font-sans ' +
  'text-sm text-content transition-colors duration-fast ' +
  'focus-visible:outline-none focus-visible:shadow-focus';

/**
 * Ficha de una actividad dentro de la agenda vertical de su sesión.
 * Solo en estado 'preparada' se edita (duración, modo), se reordena (↑/↓ o
 * mover a otra sesión) o se quita (confirmación inline de dos taps). Si ya
 * corrió (abierta/cerrada/pausada) se muestra con candado y sin controles:
 * su evidencia es parte del acta.
 *
 * Drag nativo (Lote 2b, aditivo): solo la ficha 'preparada' es draggable
 * (la bloqueada conserva su candado) y también recibe drops de otras fichas
 * para insertarlas en su posición. Los botones ↑/↓/quitar del 2a quedan
 * intactos como flujo de primera clase.
 *
 * Conexiones (Lote 4): si la herramienta declara inputs[], la ficha monta
 * los chips de {@link ConexionInputs}; y con una herramienta con inputs[]
 * seleccionada o en vuelo, muestra un halo cian cuando ELLA puede alimentarla
 * (familia + captura), aun bloqueada — su artefacto también cuenta.
 */
export const FichaActividad = memo(function FichaActividad({
  actividad,
  sesionId,
  procesoId,
  esPrimera,
  esUltima,
  opcionesSesion,
  herramientaSeleccionada,
  alSoltarActividad,
}: {
  actividad: ActividadArbol;
  sesionId: string;
  procesoId: string;
  esPrimera: boolean;
  esUltima: boolean;
  opcionesSesion: OpcionSesion[];
  /** Herramienta «en mano» (clic en el catálogo): enciende el halo de conexión. */
  herramientaSeleccionada: HerramientaCatalogo | null;
  /** Drop de OTRA ficha sobre esta: la sesión dueña dispara moverActividad. */
  alSoltarActividad: (idArrastrada: string, orden: number) => void;
}) {
  const qc = useQueryClient();
  const invalidar = () =>
    void qc.invalidateQueries({ queryKey: composicionKeys.arbol(procesoId) });

  // Duración editable: estado local que se resincroniza con el servidor.
  const [duracion, setDuracion] = useState(actividad.duracion_min?.toString() ?? '');
  useEffect(() => {
    setDuracion(actividad.duracion_min?.toString() ?? '');
  }, [actividad.duracion_min]);

  // Confirmación de quitar en dos taps (se desarma sola a los 4 s).
  const [confirmando, setConfirmando] = useState(false);
  useEffect(() => {
    if (!confirmando) return;
    const t = setTimeout(() => setConfirmando(false), 4000);
    return () => clearTimeout(t);
  }, [confirmando]);

  const editarMut = useMutation({
    mutationFn: (cuerpo: EdicionActividad) =>
      composicionApi.editarActividad(actividad.id, cuerpo),
    onSuccess: invalidar,
  });
  const moverMut = useMutation({
    mutationFn: (cuerpo: MoverActividad) =>
      composicionApi.moverActividad(actividad.id, cuerpo),
    onSuccess: invalidar,
  });
  const quitarMut = useMutation({
    mutationFn: () => composicionApi.quitarActividad(actividad.id),
    onSuccess: invalidar,
  });

  const accionError =
    (editarMut.error as ApiError | null)?.detail ??
    (moverMut.error as ApiError | null)?.detail ??
    (quitarMut.error as ApiError | null)?.detail ??
    null;

  const nombre = actividad.herramienta.nombre;
  const modos = actividad.herramienta.modos;
  const destinos = opcionesSesion.filter((o) => o.id !== sesionId);
  const bloqueada = actividad.estado !== 'preparada';

  // ---- Resaltado desde el ValidadorPanel (Lote 3) ----
  // Aplica también a la ficha bloqueada (una advertencia puede señalar una
  // actividad que ya corrió). La página apaga el contexto sola a los 3 s.
  const resaltado = useResaltado();
  const resaltada = resaltado?.actividadId === actividad.id;
  const refFicha = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (resaltada) {
      refFicha.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [resaltada, resaltado]);

  // ---- Drag nativo (Lote 2b): solo la ficha 'preparada' participa ----
  const arrastre = useArrastreCompositor();
  const itemEnVuelo = arrastre?.item ?? null;
  const enVuelo = itemEnVuelo?.tipo === 'actividad' && itemEnVuelo.id === actividad.id;
  const puedeRecibir =
    !bloqueada && itemEnVuelo?.tipo === 'actividad' && itemEnVuelo.id !== actividad.id;
  const [encima, setEncima] = useState(false);

  // ---- Conexiones (Lote 4): manifiestos completos desde la caché del catálogo ----
  // El árbol solo trae la vista compacta de la herramienta; los inputs[] viven
  // en el manifiesto C3, que CompositorPage ya tiene cacheado bajo esta llave.
  const catalogo = useQuery({
    queryKey: composicionKeys.herramientas,
    queryFn: composicionApi.herramientas,
    staleTime: 60_000,
  });
  const inputsPropios = inputsDe(
    catalogo.data?.find((h) => h.codigo === actividad.herramienta.codigo),
  );
  // Halo «esto alimenta a lo que estás por soltar»: con una herramienta CON
  // inputs[] seleccionada o en vuelo, se enciende en las fichas (aun las
  // bloqueadas: su artefacto también alimenta) que satisfacen familia+captura.
  const herramientaEnMano =
    herramientaSeleccionada ??
    (itemEnVuelo?.tipo === 'herramienta'
      ? catalogo.data?.find((h) => h.codigo === itemEnVuelo.codigo) ?? null
      : null);
  const alimenta = satisfaceAlgunInput(actividad, inputsDe(herramientaEnMano));
  const claseHalo = alimenta && !resaltada ? 'ring-1 ring-cyan' : '';

  function guardarDuracion() {
    const n = Number.parseInt(duracion, 10);
    if (!Number.isFinite(n) || n < 1) {
      setDuracion(actividad.duracion_min?.toString() ?? '');
      return;
    }
    if (n !== actividad.duracion_min) editarMut.mutate({ duracion_min: n });
  }

  if (bloqueada) {
    return (
      <div
        ref={refFicha}
        title="Esta actividad ya corrió: su evidencia es parte del acta"
        className={`rounded-md border bg-surface-sunken px-3 py-2.5 ${
          alimenta ? 'border-cyan' : 'border-border'
        } ${claseHalo} ${resaltada ? 'ring-2 ring-warning' : ''}`}
      >
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 shrink-0 text-content-muted" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate font-sans text-sm font-semibold text-content">
            {nombre}
          </span>
          {actividad.estado === 'cerrada' ? (
            <Badge variant="success">Cerrada</Badge>
          ) : actividad.estado === 'pausada' ? (
            <Badge variant="warning">En pausa</Badge>
          ) : (
            <Badge variant="primary">En curso</Badge>
          )}
        </div>
        <p className="mt-1 font-sans text-xs tabular-nums text-content-muted">
          {actividad.duracion_min != null ? `${actividad.duracion_min} min` : 'Sin duración'}
          {actividad.modo ? ` · ${actividad.modo}` : ''}
        </p>
      </div>
    );
  }

  return (
    <div
      ref={refFicha}
      draggable={arrastre != null}
      onDragStart={arrastre?.arrastrarActividad(actividad.id, sesionId)}
      onDragEnd={arrastre?.terminarArrastre}
      /* Vía del dedo: sostener un cuarto de segundo levanta la ficha. Solo la
         'preparada' se puede mover, igual que con el arrastre nativo. */
      onPointerDown={
        arrastre
          ? arrastre.puntero.tomar({ tipo: 'actividad', id: actividad.id, sesionId }, nombre)
          : undefined
      }
      onDragOver={(e) => {
        if (!puedeRecibir) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setEncima(true);
      }}
      onDragLeave={() => setEncima(false)}
      onDrop={(e) => {
        setEncima(false);
        if (!puedeRecibir || itemEnVuelo?.tipo !== 'actividad') return;
        e.preventDefault();
        // Que NO burbujee a la TarjetaSesion: un drop = UNA mutación.
        e.stopPropagation();
        alSoltarActividad(itemEnVuelo.id, actividad.orden);
        arrastre?.terminarArrastre();
      }}
      className={`rounded-md border px-3 py-2.5 transition-colors duration-fast ${
        encima && puedeRecibir
          ? 'border-primary bg-surface-raised'
          : `${alimenta ? 'border-cyan' : 'border-border'} bg-surface-sunken`
      } ${arrastre ? 'cursor-grab active:cursor-grabbing' : ''} ${
        enVuelo ? 'opacity-40' : ''
      } ${claseHalo} ${resaltada ? 'ring-2 ring-warning' : ''}`}
    >
      <div className="flex items-start justify-between gap-1">
        <span className="min-w-0 flex-1 pt-2 font-sans text-sm font-semibold text-content">
          {nombre}
        </span>

        <div className="flex shrink-0 items-center">
          <button
            type="button"
            onClick={() => moverMut.mutate({ sesion_id: sesionId, orden: actividad.orden - 1 })}
            disabled={esPrimera || moverMut.isPending}
            aria-label={`Subir «${nombre}» un lugar`}
            title="Subir un lugar"
            className={botonIcono}
          >
            <ArrowUp className="h-4 w-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => moverMut.mutate({ sesion_id: sesionId, orden: actividad.orden + 1 })}
            disabled={esUltima || moverMut.isPending}
            aria-label={`Bajar «${nombre}» un lugar`}
            title="Bajar un lugar"
            className={botonIcono}
          >
            <ArrowDown className="h-4 w-4" aria-hidden="true" />
          </button>

          {confirmando ? (
            <button
              type="button"
              onClick={() => quitarMut.mutate()}
              disabled={quitarMut.isPending}
              className="flex min-h-touch items-center gap-1 rounded-md border border-error px-2 font-sans text-xs font-semibold text-error transition-colors duration-fast hover:bg-surface-raised focus-visible:outline-none focus-visible:shadow-focus disabled:pointer-events-none disabled:opacity-50"
            >
              {quitarMut.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              ¿Quitar?
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              aria-label={`Quitar «${nombre}» de la sesión`}
              title="Quitar de la sesión (te pide confirmar)"
              className={botonIcono}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <label className="flex items-center gap-1.5 font-sans text-xs text-content-muted">
          <Clock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <input
            type="number"
            min={1}
            inputMode="numeric"
            value={duracion}
            onChange={(e) => setDuracion(e.target.value)}
            onBlur={guardarDuracion}
            aria-label={`Duración en minutos de «${nombre}»`}
            className={`${claseCampo} w-16 text-right tabular-nums`}
          />
          min
        </label>

        {modos.length > 0 && (
          <select
            value={actividad.modo ?? ''}
            onChange={(e) => {
              if (e.target.value) editarMut.mutate({ modo: e.target.value });
            }}
            disabled={editarMut.isPending}
            aria-label={`Modo de «${nombre}»`}
            className={claseCampo}
          >
            {actividad.modo == null && <option value="">Elegir modo…</option>}
            {modos.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        )}
      </div>

      {inputsPropios.length > 0 && (
        <ConexionInputs
          actividad={actividad}
          procesoId={procesoId}
          inputs={inputsPropios}
        />
      )}

      {destinos.length > 0 && (
        <div className="mt-2">
          <select
            value=""
            onChange={(e) => {
              const destino = destinos.find((o) => o.id === e.target.value);
              if (destino) {
                moverMut.mutate({
                  sesion_id: destino.id,
                  orden: destino.totalActividades + 1,
                });
              }
            }}
            disabled={moverMut.isPending}
            aria-label={`Mover «${nombre}» a otra sesión`}
            className={`${claseCampo} w-full text-content-muted`}
          >
            <option value="">Mover a otra sesión…</option>
            {destinos.map((o) => (
              <option key={o.id} value={o.id}>
                {o.etiqueta}
              </option>
            ))}
          </select>
        </div>
      )}

      {accionError && (
        <p className="mt-2 flex items-start gap-1.5 font-sans text-xs text-error">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {accionError}
        </p>
      )}
    </div>
  );
});
